<template>
  <section v-if="projects?.length" class="projects pe-section">
    <h3>
      Projects
      <SectionActions v-show="isPowerEdit" class="actions" section="projects" />
    </h3>
    <template v-for="(project, index) in projects" :key="project.name">
      <section
        v-if="project.name || project.description || project.roles?.length"
        class="project"
      >
        <ItemActions
          v-show="isPowerEdit"
          class="itemActions"
          :index="index"
          section="projects"
          :uuid="project.uuid"
          :actions="['edit', 'delete']"
        />
        <section class="title">
          <div class="details">
            <div class="position">
              <a :href="getNewTabUrl(project.url)" target="_blank">
                <div
                  @click.prevent
                  v-editable="isPowerEdit"
                  :data-target-path="`resume.projects.${index}.name`"
                  class="name"
                  v-text="project.name"
                ></div>
              </a>
              <div class="role">
                <span
                  v-if="project.roles?.length"
                  v-text="project.roles?.join(', ')"
                ></span>
              </div>
            </div>
          </div>
          <div class="dates">
            <div>
              <DateDisplay
                :date="project.startDate"
                :updatePath="`resume.projects.${index}.startDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(project.startDate, project.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(project.startDate, project.endDate)
                  )
                "
              />
              <span v-if="project.startDate"> - </span>
              <DateDisplay
                :date="project.endDate"
                :updatePath="`resume.projects.${index}.endDate`"
                hasCurrentOption
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(project.startDate, project.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(project.startDate, project.endDate)
                  )
                "
              />
            </div>
          </div>
        </section>
        <section class="description">
          <div
            class="summary"
            v-word-count
            v-editable="isPowerEdit"
            :data-target-path="`resume.projects.${index}.description`"
            v-text="project.description"
            @click="
              evt =>
                openRephrasePopup(
                  `resume.projects.${index}.description`,
                  evt.target
                )
            "
          ></div>
          <Highlights
            v-if="project.highlights"
            :highlights="project.highlights"
            :targetPath="`resume.projects.${index}.highlights`"
          />
        </section>
      </section>
    </template>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Resume } from '@/types/resume/v1'
import Highlights from './Highlights.vue'
import DateDisplay from './support/DateDisplay.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import { checkDates } from '@/lib/dates/dateHelper'
import { usePowerEditStore } from '@/store/pinia/powerEdit'
import { getNewTabUrl } from '@/lib/urls'

export default defineComponent({
  name: 'Projects',
  components: {
    DateDisplay,
    SectionActions,
    ItemActions,
    Highlights,
  },
  props: {
    projects: {
      type: Object as PropType<Resume.Project[]>,
      required: false,
    },
  },
  setup() {
    const store = useStructuredResumeStore()
    const powerEditStore = usePowerEditStore()
    const { closePopup, openRephrasePopup } = powerEditStore

    return {
      isPowerEdit: store.isPowerEdit,
      checkDates,
      closePopup,
      openRephrasePopup,
      getNewTabUrl,
    }
  },
})
</script>

<style lang="postcss" scoped>
.projects {
  @apply text-base-em leading-[inherit] pt-6-em block;
  a {
    @apply text-inherit cursor-text;
  }
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .project {
    @apply pt-4-em relative;
    .itemActions {
      @apply hidden;
    }
    &:hover {
      .itemActions {
        @apply flex;
      }
    }
    .title {
      @apply flex flex-row justify-between items-start mb-2-em;
      .details {
        .name {
          @apply font-semibold;
        }
        .role {
          @apply italic;
        }
      }
    }
    .dates {
      @apply text-sm-em;
    }
    .description {
      @apply leading-[inherit] text-sm-em;
      .summary {
        @apply mb-2-em whitespace-pre-wrap;
      }
      .highlights {
        ul {
          @apply list-disc list-outside ml-4-em;
        }
      }
    }
  }
}
</style>
